import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Image,
    IconButton,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {getUrl} from '../../../../../utils/cloudinary-utils'
import getCloudinaryVideoUrl from '../../../../cloudinary-media/video'
import breakpoints from '../../../../../theme/foundations/breakpoints'

const getVideoSource = ({src, fallbackAspectRatio, aspectRatio}) => {
    if (src?.indexOf('ar_') !== -1) {
        return src
    }

    return getCloudinaryVideoUrl({
        src,
        aspectRatio: aspectRatio || fallbackAspectRatio
    })
}

const Video = ({
    desktopVideo,
    desktopVideoThumbnail,
    mobileVideo,
    mobileVideoThumbnail,
    videoAlt,
    videoEnableSound,
    videoDisableAutoPlay,
    variant,
    mediaSize,
    desktopMediaPosition,
    mobileMediaPosition,
    onButtonClick,
    cloudinaryAspectRatio
}) => {
    const videoRef = useRef(null)
    const [isPlaying, setIsPlaying] = useState(!videoDisableAutoPlay)
    const [isMuted, setIsMuted] = useState(true)

    const widthDesktop = desktopMediaPosition === 'full' ? 1920 : 960
    const heightMobile = mobileMediaPosition === 'full' ? 654 : 327
    const isMobile = useBreakpointValue({base: true, md: false})
    const mobilePoster = !videoDisableAutoPlay
        ? mobileVideo?.replace('mp4', 'webp')
        : mobileVideoThumbnail ?? mobileVideo?.replace('mp4', 'webp')
    const desktopPoster = !videoDisableAutoPlay
        ? desktopVideo?.replace('mp4', 'webp')
        : desktopVideoThumbnail ?? desktopVideo?.replace('mp4', 'webp')
    const poster = isMobile ? mobilePoster : desktopPoster

    const sizeDesktop =
        variant === 'hero'
            ? mediaSize === 'hero'
                ? {width: widthDesktop, height: 900}
                : {width: widthDesktop, height: 480}
            : {width: widthDesktop, height: 684}
    const sizeMobile = {width: 414, height: heightMobile}
    const cssAspectRatio = cloudinaryAspectRatio?.replaceAll(':', '/')

    const togglePlayPause = (videoRef) => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause()
            } else {
                videoRef.current.play()
            }
            setIsPlaying(!isPlaying)
        }
    }

    const toggleMuteUnmute = (videoRef) => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted
            setIsMuted(!isMuted)
        }
    }

    return (
        <Box
            className="video-container"
            position="relative"
            width="100%"
            height="100%"
            sx={{
                video: {
                    aspectRatio: {
                        base: cssAspectRatio || `${sizeMobile.width}/${sizeMobile.height}`,
                        md: cssAspectRatio || `${sizeDesktop.width}/${sizeDesktop.height}`
                    }
                }
            }}
        >
            <link rel="preload" as="image" href={poster} fetchPriority="high" />
            <video
                ref={videoRef}
                className="component-video"
                poster={poster}
                {...(isPlaying && {autoPlay: true})}
                muted
                alt={videoAlt}
                loop
                playsInline
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                }}
                onClick={onButtonClick}
            >
                <source
                    src={getVideoSource({
                        src: mobileVideo,
                        aspectRatio: cloudinaryAspectRatio,
                        fallbackAspectRatio: `${sizeMobile.width}:${sizeMobile.height}`
                    })}
                    media={`(max-width: ${breakpoints.md})`}
                />
                <source
                    src={getVideoSource({
                        src: desktopVideo,
                        aspectRatio: cloudinaryAspectRatio,
                        fallbackAspectRatio: `${sizeDesktop.width}:${sizeDesktop.height}`
                    })}
                />
            </video>

            <Box position="absolute" top="20px" right="20px" gap="10px" display="grid" zIndex="2">
                <IconButton
                    onClick={() => togglePlayPause(videoRef)}
                    aria-label="Toggle Play/Pause"
                    className="play-pause-btn"
                    icon={
                        <Image
                            width="30px"
                            height="30px"
                            src={
                                isPlaying
                                    ? getUrl('UIImages/images/video-pause-hover.svg')
                                    : getUrl('UIImages/images/video-play-hover.svg')
                            }
                            alt="Pause/ Play Icon"
                        />
                    }
                    variant="unstyled"
                    height="auto"
                    minHeight="auto"
                    minWidth="auto"
                    display="inline-flex"
                    gap="10px"
                    zIndex="1"
                    opacity="0.8"
                    _hover={{
                        opacity: '1'
                    }}
                />

                {videoEnableSound && (
                    <IconButton
                        onClick={() => toggleMuteUnmute(videoRef)}
                        aria-label="Toggle Mute/Unmute"
                        icon={
                            <Image
                                width="30px"
                                height="30px"
                                src={
                                    isMuted
                                        ? getUrl('UIImages/images/mute-hover.svg')
                                        : getUrl('UIImages/images/unmute-hover.svg')
                                }
                                alt="Mute/ Unmute Icon"
                            />
                        }
                        variant="unstyled"
                        height="auto"
                        minHeight="auto"
                        minWidth="auto"
                        gap="10px"
                        display="inline-flex"
                        zIndex="1"
                        opacity="0.8"
                        _hover={{
                            opacity: '1'
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

Video.propTypes = {
    desktopVideo: PropTypes.string.isRequired,
    mobileVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    videoAlt: PropTypes.string,
    videoEnableSound: PropTypes.bool,
    videoDisableAutoPlay: PropTypes.bool,
    variant: PropTypes.string,
    mediaSize: PropTypes.string,
    desktopMediaPosition: PropTypes.string,
    mobileMediaPosition: PropTypes.string,
    onButtonClick: PropTypes.func,
    cloudinaryAspectRatio: PropTypes.string
}

export default Video
